/**
 * Auto-subscribe to push notifications
 * Runs when profile.id changes (user logs in/out or profile loads)
 */
import { useEffect, useRef } from "react";
import useProfile from "./use-profile";
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  sendSubscriptionToBackend,
} from "@/services/push/pushService";

const STORAGE_KEY = "push_subscribed_user_id";
export { STORAGE_KEY };
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function useAutoSubscribe() {
  const { profile } = useProfile();
  const hasSubscribed = useRef<string | null>(
    localStorage.getItem(STORAGE_KEY) || null,
  );

  // iOS browser detection - but we still call hooks
  const isIOSBrowser =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    typeof window.matchMedia !== "undefined" &&
    !window.matchMedia("(display-mode: standalone)").matches;

  useEffect(() => {
    // Skip entirely on iOS browser (not PWA) - causes crashes on iOS
    if (isIOSBrowser) return;

    if (!profile?.id) return;
    if (hasSubscribed.current === profile.id) return;

    const trySubscribe = async (retries = 0) => {
      if (!isPushSupported()) return;
      if (getNotificationPermission() !== "granted") return;

      // On iOS, only try if running as standalone PWA
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        const isStandalone =
          typeof window.matchMedia !== "undefined" &&
          window.matchMedia("(display-mode: standalone)").matches;
        if (!isStandalone) return;
      }

      // Safety check: ensure serviceWorker exists before accessing
      if (!("serviceWorker" in navigator)) return;

      // Check if serviceWorker is ready with timeout to prevent hanging
      let reg: ServiceWorkerRegistration | null = null;
      try {
        reg = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>((_, reject) =>
            setTimeout(() => reject(new Error("SW timeout")), 3000),
          ),
        ]);
      } catch {
        // Service worker not available or timeout
        return;
      }

      if (!reg) return;

      try {
        let subscription = await reg.pushManager.getSubscription();

        if (!subscription) {
          subscription = await subscribeToPush(reg);
          if (!subscription) return;
        }

        const success = await sendSubscriptionToBackend(subscription);
        if (success) {
          hasSubscribed.current = profile.id;
          localStorage.setItem(STORAGE_KEY, profile.id);
        } else if (retries < MAX_RETRIES) {
          // Retry with exponential backoff
          setTimeout(
            () => {
              trySubscribe(retries + 1);
            },
            RETRY_DELAY * Math.pow(2, retries),
          );
        }
      } catch {
        // Silently fail
      }
    };

    if (Notification.permission === "default") {
      // Only request permission if Push is supported and secure context
      if (isPushSupported()) {
        Notification.requestPermission();
      }
    }

    trySubscribe();

    // Listen for permission changes - with proper cleanup
    let permissionQuery: PermissionStatus | null = null;

    const handlePermissionChange = () => {
      if (getNotificationPermission() === "granted") {
        trySubscribe();
      }
    };

    navigator.permissions
      ?.query({ name: "notifications" })
      .then((result) => {
        permissionQuery = result;
        result.addEventListener("change", handlePermissionChange);
      })
      .catch(() => {
        // Silently ignore if notifications permission API is not supported
      });

    return () => {
      if (permissionQuery) {
        permissionQuery.removeEventListener("change", handlePermissionChange);
      }
    };
  }, [profile?.id]);
}
